/**
 * `<page-audit>` — the audit explorer, routed at `/instances/:id/audit`.
 *
 * This is the screen `concept.md` §6.5 opens with: an analyst reports a 403, and a
 * data steward filters thousands of records down to the one record that explains
 * it. It is also the screen §6.6 hands an auditor, which is why the chain and its
 * verification live here rather than in a settings drawer.
 *
 * Implements:
 *
 *   - `SC-F804` — search and filter by subject, time, decision, table, catalog,
 *     error code, event type, policy and origin, keyset-paginated, with every
 *     filter expressible in the URL so a filtered view is a shareable link. The
 *     record detail opens in a drawer rather than a route, so the filtered list a
 *     reader assembled survives being read.
 *   - `SC-F803` / `SC-F807` — the chain: genesis anchor, the object ledger, the
 *     ordered list of chain spans, the continuity rollup, and an on-demand
 *     verification that reports the **anchor used**, the file and record counts and
 *     the last verified hash, and on failure the exact file and line.
 *   - `SC-F805` — the export, step-up gated, with the JSONL-cannot-be-filtered rule
 *     stated in the dialog rather than discovered as a `422`.
 *   - `SC-F821` — the profile badge on every tab, and the honest empty states the
 *     Minimized and Customer-held profiles oblige.
 *   - `SC-F823` — the live tail, with its two limits carried on the surface itself.
 *
 * Stories: `SC-S800` (a gap is shown as a known gap here, not left invisible),
 * `SC-S804`, `SC-S805`, `SC-S806` (the ingest watermark), `SC-S809`, `SC-S812`,
 * `SC-S813`, `SC-S907` (the two zero states, and the error state with its
 * `request_id`).
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";

import "../components/index.js";
import {
  decisionVariant,
  toast,
  type BnKvCopyDetail,
  type BnColumn,
  type BnKvRow,
  type BnPageChangeDetail,
  type BnRow,
  type BnRowClickDetail,
  type BnSelectChangeDetail,
  type BnSelectOption,
  type BnTab,
  type BnTable,
  type BnTabsChangeDetail,
} from "../components/index.js";

import {
  AUDIT_DECISIONS,
  AUDIT_ORIGINS,
  EMITTED_EVENT_TYPES,
  TAIL_DEFAULT_N,
  TAIL_POLL_MS,
  TEXT_SEARCH_WINDOW_DAYS,
  exportAudit,
  getAuditChain,
  getAuditRecord,
  getAuditTail,
  searchAudit,
  verifyAuditChain,
  type AuditDecision,
  type AuditFilter,
  type AuditRecord,
  type AuditRecordDetail,
  type AuditSearchPage,
  type ChainStatus,
  type ExportFormat,
  type ExportResult,
  type TailRecord,
  type TailResponse,
  type VerifyResponse,
} from "../api/audit.js";
import { getInstance, type Instance } from "../api/instances.js";
import { confirmStepUpOtp, requestStepUpOtp, verifyWithPasskey } from "../api/auth.js";
import { jobFailure, watchJob } from "../api/jobs.js";
import { ApiError, parseError } from "../api/errors.js";
import { hrefFor, navigate } from "../nav.js";
import { recordDetailTemplate } from "./audit-record-detail.js";
import {
  RANGE_PRESETS,
  auditSharedCss,
  ago,
  bytes,
  chainBadge,
  count,
  duration,
  errorCard,
  errorCodeOptions,
  fromIngestStatus,
  hoursBetween,
  profileBadge,
  projectionNotice,
  rangeFor,
  shortHash,
  when,
  whenUtc,
  watermarkLine,
} from "./audit-shared.js";

/** TabId is the three surfaces of this page. `?tab=` carries it so a link can
 * point at the chain panel, which is what a support conversation points at. */
type TabId = "records" | "chain" | "tail";

const COLUMNS: BnColumn[] = [
  { key: "ts", label: "Time", width: "1px" },
  { key: "decision", label: "Decision", width: "1px" },
  { key: "subject", label: "Subject" },
  { key: "tables", label: "Tables" },
  { key: "durationCell", label: "Duration", width: "1px", align: "right" },
  { key: "rows", label: "Rows", width: "1px", align: "right" },
  { key: "policies", label: "Policies", width: "1px", align: "right" },
  { key: "chain", label: "Chain", width: "1px" },
];

const TAIL_COLUMNS: BnColumn[] = [
  { key: "ts", label: "Time", width: "1px" },
  { key: "decision", label: "Decision", width: "1px" },
  { key: "subject", label: "Subject" },
  { key: "tables", label: "Tables" },
  { key: "policies", label: "Policies" },
  { key: "durationCell", label: "Duration", width: "1px", align: "right" },
];

/** How many tail records the widget keeps on screen. The endpoint is last-N and
 * lossy, so an unbounded buffer would only make a debugging aid heavy. */
const TAIL_BUFFER = 500;

/** ANY is the "no opinion" value for a tri-state picker. It is a sentinel rather
 * than the empty string because `bn-select` reads an empty value as "nothing
 * selected" and falls back to its placeholder, which would leave the control
 * looking unset after a deliberate choice. It never reaches a request. */
const ANY = "any";

/** Keystroke debounce on the text filters, so typing a table name is one query. */
const TYPE_DEBOUNCE_MS = 350;

@customElement("page-audit")
export class PageAudit extends LitElement {
  @property() instanceId = "";

  @state() private tab: TabId = "records";
  @state() private instance: Instance | null = null;

  /** The filter, materialised from the URL. `from` and `to` are always explicit:
   * the server would default them, but two panels on one screen must not disagree
   * about "now" and a shared link must mean the same range tomorrow. */
  @state() private filter: AuditFilter = {};
  @state() private presetId = "24h";
  @state() private errorCodes: BnSelectOption[] = [];

  @state() private page: AuditSearchPage | null = null;
  @state() private loading = true;
  @state() private error: ApiError | null = null;
  private cursor: string | null = null;

  @state() private detail: AuditRecordDetail | null = null;
  @state() private detailLoading = false;
  @state() private detailError: ApiError | null = null;
  @state() private drawerOpen = false;
  /** The hash the drawer was opened with, kept so a retry after a failed load has
   * something to retry — `detail` is null in exactly that case. */
  private detailHash = "";

  @state() private chain: ChainStatus | null = null;
  @state() private chainLoading = false;
  @state() private chainError: ApiError | null = null;
  @state() private verifyResult: VerifyResponse | null = null;
  @state() private verifying = false;
  @state() private verifyStep = "";
  @state() private verifyError: ApiError | null = null;

  @state() private tailRunning = false;
  @state() private tailItems: TailRecord[] = [];
  @state() private tailMeta: TailResponse | null = null;
  @state() private tailError: ApiError | null = null;

  @state() private exportOpen = false;
  @state() private exportFormat: ExportFormat = "jsonl";
  @state() private exporting = false;
  @state() private exportError: ApiError | null = null;
  @state() private exportResult: ExportResult | null = null;
  @state() private stepUpPhase: "" | "needed" | "otp" = "";
  @state() private otpId = "";
  @state() private otpCode = "";

  @query("bn-table#records") private tableEl!: BnTable | null;

  private typeTimer = 0;
  private tailTimer = 0;
  /** Monotonic request id. A filter change fires several fetches and the network
   * is free to answer them out of order; only the newest result may render
   * (`SC-S808`'s fourth criterion, which applies here for the same reason). */
  private seq = 0;

  static override styles = [
    auditSharedCss,
    css`
      .tab-body {
        padding-top: var(--bino-space-md);
      }
      bn-card + bn-card,
      bn-card + bn-panel,
      bn-panel + bn-card {
        margin-top: var(--bino-space-md);
      }
      .span-grid {
        display: grid;
        gap: var(--bino-space-sm);
      }
      .span {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--bino-space-sm);
        padding: var(--bino-space-sm);
        border: 1px solid var(--bino-border);
        border-radius: var(--bino-radius-sm);
        background: var(--bino-surface-subtle);
        font-size: var(--bino-font-size-md);
      }
      table.ledger {
        width: 100%;
        border-collapse: collapse;
        font-size: var(--bino-font-size-sm);
      }
      table.ledger th,
      table.ledger td {
        text-align: left;
        padding: var(--bino-space-xs) var(--bino-space-sm);
        border-bottom: 1px solid var(--bino-border);
        white-space: nowrap;
      }
      table.ledger th {
        color: var(--bino-text-secondary);
        font-weight: 600;
      }
      table.ledger td {
        font-family: var(--bino-font-mono);
        font-size: var(--bino-font-size-xs);
      }
      .scroll {
        overflow-x: auto;
      }
      .cell-sub {
        font-family: var(--bino-font-mono);
        font-size: var(--bino-font-size-base);
      }
      .more {
        margin-bottom: var(--bino-space-md);
      }
      .more summary {
        cursor: pointer;
        width: fit-content;
        font-size: var(--bino-font-size-sm);
        color: var(--bino-text-secondary);
      }
      .more summary:focus-visible {
        outline: none;
        border-radius: var(--bino-radius-sm);
        box-shadow: var(--bino-focus-ring-shadow);
      }
      .drawer-head {
        margin-bottom: var(--bino-space-md);
      }
    `,
  ];

  /* ── lifecycle ───────────────────────────────────────────────────────── */

  override connectedCallback() {
    super.connectedCallback();
    this.readUrl();
    void this.loadInstance();
    void errorCodeOptions().then((opts) => (this.errorCodes = opts));
    void this.load(null);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.typeTimer);
    this.stopTail();
  }

  /**
   * readUrl restores the whole view from the query string.
   *
   * `04` §7.1's second rule is that every filter is expressible in a URL, which is
   * what makes "saved views" a thin feature rather than a subsystem — and what lets
   * a data steward paste the search that found a denial into a ticket.
   */
  private readUrl() {
    const p = new URLSearchParams(location.search);
    const tab = p.get("tab");
    if (tab === "records" || tab === "chain" || tab === "tail") this.tab = tab;
    if (this.tab === "chain") void this.loadChain();

    const preset = p.get("preset") ?? "24h";
    const from = p.get("from") ?? undefined;
    const to = p.get("to") ?? undefined;
    const known = RANGE_PRESETS.find((r) => r.id === preset);
    if (known && !(from && to)) {
      this.presetId = known.id;
      const r = rangeFor(known.hours);
      this.filter = { ...this.filter, from: r.from, to: r.to };
    } else {
      this.presetId = known && from && to ? known.id : "custom";
      this.filter = { ...this.filter, from, to };
    }

    const list = (k: string) => (p.getAll(k).length ? p.getAll(k) : undefined);
    const truncated = p.get("truncated");
    const min = p.get("min_duration_ms");
    const sort = p.get("sort");
    this.filter = {
      ...this.filter,
      subject: list("subject"),
      issuer: list("issuer"),
      method: list("method"),
      decision: list("decision") as AuditDecision[] | undefined,
      error_code: list("error_code"),
      event_type: list("event_type"),
      catalog: list("catalog"),
      schema: list("schema"),
      table: list("table"),
      policy: list("policy"),
      query_id: list("query_id"),
      origin: list("origin"),
      group: list("group"),
      truncated: truncated === null ? undefined : truncated === "true",
      min_duration_ms: min ? Number(min) : undefined,
      q: p.get("q") ?? undefined,
      sort: sort === "ts" || sort === "-duration_ms" ? sort : "-ts",
    };
  }

  /** writeUrl mirrors the view back into the address bar. It replaces rather than
   * pushes: a filter is a view of this route, not a route of its own, and it should
   * not cost a back-button press per keystroke to undo. */
  private writeUrl() {
    const p = new URLSearchParams();
    if (this.tab !== "records") p.set("tab", this.tab);
    p.set("preset", this.presetId);
    if (this.presetId === "custom") {
      if (this.filter.from) p.set("from", this.filter.from);
      if (this.filter.to) p.set("to", this.filter.to);
    }
    for (const key of [
      "subject",
      "issuer",
      "method",
      "decision",
      "error_code",
      "event_type",
      "catalog",
      "schema",
      "table",
      "policy",
      "query_id",
      "origin",
      "group",
    ] as const) {
      for (const v of (this.filter[key] ?? []) as string[]) p.append(key, v);
    }
    if (this.filter.truncated !== undefined) p.set("truncated", String(this.filter.truncated));
    if (this.filter.min_duration_ms) p.set("min_duration_ms", String(this.filter.min_duration_ms));
    if (this.filter.q) p.set("q", this.filter.q);
    if (this.filter.sort && this.filter.sort !== "-ts") p.set("sort", this.filter.sort);
    navigate(`${location.pathname}?${p.toString()}`, { replace: true });
  }

  private async loadInstance() {
    try {
      this.instance = await getInstance(this.instanceId);
      // Under the Customer-held profile there are no records to list at all, so the
      // chain panel is the surface with something to say and the page opens on it
      // rather than on a refusal (`SC-F822`).
      if (this.instance.audit_profile === "customer_held" && this.tab === "records") {
        this.tab = "chain";
        void this.loadChain();
      }
    } catch {
      // The header degrades to the record id. Losing the instance name costs a
      // breadcrumb, not the page.
      this.instance = null;
    }
  }

  private async load(cursor: string | null) {
    const mine = ++this.seq;
    this.loading = true;
    try {
      const page = await searchAudit(this.instanceId, { ...this.filter, cursor: cursor ?? undefined });
      if (mine !== this.seq) return;
      this.page = page;
      this.cursor = cursor;
      this.error = null;
    } catch (err) {
      if (mine !== this.seq) return;
      this.error = parseError(err);
      this.page = null;
    } finally {
      if (mine === this.seq) this.loading = false;
    }
  }

  /** refilter restarts at page one. A keyset cursor is only valid for the query
   * that minted it, so replaying one across a filter change is a 422. */
  private refilter() {
    this.tableEl?.resetPagination();
    this.writeUrl();
    void this.load(null);
  }

  /* ── filter controls ─────────────────────────────────────────────────── */

  private setList(key: keyof AuditFilter, values: string[]) {
    this.filter = { ...this.filter, [key]: values.length ? values : undefined };
    this.refilter();
  }

  private setText(key: keyof AuditFilter, value: string) {
    const v = value.trim();
    this.filter = { ...this.filter, [key]: v ? [v] : undefined };
    clearTimeout(this.typeTimer);
    this.typeTimer = window.setTimeout(() => this.refilter(), TYPE_DEBOUNCE_MS);
  }

  private onPreset(e: CustomEvent<BnSelectChangeDetail>) {
    const id = e.detail.value;
    this.presetId = id;
    const known = RANGE_PRESETS.find((r) => r.id === id);
    if (known) {
      const r = rangeFor(known.hours);
      this.filter = { ...this.filter, from: r.from, to: r.to };
    }
    this.refilter();
  }

  private onBound(which: "from" | "to", value: string) {
    // `datetime-local` yields a naive timestamp, and the server refuses one: a
    // bound with no offset is 422 rather than guessed. It is read as the reader's
    // own wall clock and converted, which is the only reading that matches what
    // they typed.
    const d = value ? new Date(value) : null;
    this.filter = {
      ...this.filter,
      [which]: d && !Number.isNaN(d.getTime()) ? d.toISOString() : undefined,
    };
    clearTimeout(this.typeTimer);
    this.typeTimer = window.setTimeout(() => this.refilter(), TYPE_DEBOUNCE_MS);
  }

  private clearFilters() {
    const r = rangeFor(24);
    this.presetId = "24h";
    this.filter = { from: r.from, to: r.to, sort: "-ts" };
    this.refilter();
  }

  /** localInput renders an ISO instant as the value `<input type="datetime-local">`
   * wants — the reader's own wall clock, minute precision. */
  private localInput(iso: string | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private get textSearchBlocked(): string {
    const profile = this.page?.profile ?? this.instance?.audit_profile;
    if (profile === "minimized") {
      return "Unavailable on this instance: the Minimized audit profile captures no SQL text at the source, by your organization's own choice — so there is nothing to match.";
    }
    if (profile === "customer_held") {
      return "Unavailable: Sluice Cloud stores no records for a Customer-held instance.";
    }
    const from = this.filter.from;
    const to = this.filter.to;
    if (from && to && hoursBetween(from, to) > TEXT_SEARCH_WINDOW_DAYS * 24) {
      return `Text search must scan, so it is capped at ${TEXT_SEARCH_WINDOW_DAYS} days. Narrow the range to use it.`;
    }
    return "";
  }

  private renderToolbar() {
    const f = this.filter;
    const blocked = this.textSearchBlocked;
    const presetOptions: BnSelectOption[] = [
      ...RANGE_PRESETS.map((r) => ({ value: r.id, label: r.label })),
      { value: "custom", label: "Custom range" },
    ];
    return html`
      <div class="toolbar">
        <bn-field class="field-md" label="Time range">
          <bn-select .options=${presetOptions} .value=${this.presetId} @change=${this.onPreset}></bn-select>
        </bn-field>
        ${this.presetId === "custom"
          ? html`
              <bn-field class="field-md" label="From (inclusive)">
                <bn-input
                  type="datetime-local"
                  .value=${this.localInput(f.from)}
                  @input=${(e: CustomEvent<{ value: string }>) => this.onBound("from", e.detail.value)}
                ></bn-input>
              </bn-field>
              <bn-field class="field-md" label="To (exclusive)">
                <bn-input
                  type="datetime-local"
                  .value=${this.localInput(f.to)}
                  @input=${(e: CustomEvent<{ value: string }>) => this.onBound("to", e.detail.value)}
                ></bn-input>
              </bn-field>
            `
          : nothing}
        <bn-field class="field-md" label="Decision">
          <bn-select
            multiple
            placeholder="Any decision"
            .options=${AUDIT_DECISIONS.map((d) => ({ value: d, label: d }))}
            .values=${(f.decision ?? []) as string[]}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) => this.setList("decision", e.detail.values)}
          ></bn-select>
        </bn-field>
        <bn-field class="field-md" label="Error code">
          <bn-select
            multiple
            filterable
            placeholder="Any code"
            .options=${this.errorCodes}
            .values=${f.error_code ?? []}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) => this.setList("error_code", e.detail.values)}
          ></bn-select>
        </bn-field>
        <bn-field class="field-grow" label="Subject">
          <bn-input
            type="search"
            placeholder="Subject id"
            .value=${(f.subject ?? [])[0] ?? ""}
            @input=${(e: CustomEvent<{ value: string }>) => this.setText("subject", e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field class="field-grow" label="Table">
          <bn-input
            type="search"
            placeholder="catalog.schema.table"
            .value=${(f.table ?? [])[0] ?? ""}
            @input=${(e: CustomEvent<{ value: string }>) => this.setText("table", e.detail.value)}
          ></bn-input>
        </bn-field>
        <div class="toolbar-end">
          <bn-button size="sm" ?loading=${this.loading} @click=${() => void this.load(this.cursor)}>
            Refresh
          </bn-button>
          <bn-button size="sm" @click=${this.clearFilters}>Clear</bn-button>
        </div>
      </div>
      <details class="more">
        <summary>More filters</summary>
        <div class="toolbar">
          <bn-field class="field-md" label="Event type">
            <bn-select
              multiple
              placeholder="Any event"
              .options=${EMITTED_EVENT_TYPES.map((t) => ({ value: t, label: t }))}
              .values=${f.event_type ?? []}
              @change=${(e: CustomEvent<BnSelectChangeDetail>) => this.setList("event_type", e.detail.values)}
            ></bn-select>
          </bn-field>
          <bn-field class="field-md" label="Origin">
            <bn-select
              multiple
              placeholder="Any origin"
              .options=${AUDIT_ORIGINS.map((o) => ({ value: o, label: o }))}
              .values=${f.origin ?? []}
              @change=${(e: CustomEvent<BnSelectChangeDetail>) => this.setList("origin", e.detail.values)}
            ></bn-select>
          </bn-field>
          <bn-field class="field-grow" label="Catalog">
            <bn-input
              type="search"
              .value=${(f.catalog ?? [])[0] ?? ""}
              @input=${(e: CustomEvent<{ value: string }>) => this.setText("catalog", e.detail.value)}
            ></bn-input>
          </bn-field>
          <bn-field class="field-grow" label="Schema">
            <bn-input
              type="search"
              .value=${(f.schema ?? [])[0] ?? ""}
              @input=${(e: CustomEvent<{ value: string }>) => this.setText("schema", e.detail.value)}
            ></bn-input>
          </bn-field>
          <bn-field class="field-grow" label="Applied policy">
            <bn-input
              type="search"
              placeholder="Policy name"
              .value=${(f.policy ?? [])[0] ?? ""}
              @input=${(e: CustomEvent<{ value: string }>) => this.setText("policy", e.detail.value)}
            ></bn-input>
          </bn-field>
          <bn-field class="field-grow" label="Query id">
            <bn-input
              type="search"
              .value=${(f.query_id ?? [])[0] ?? ""}
              @input=${(e: CustomEvent<{ value: string }>) => this.setText("query_id", e.detail.value)}
            ></bn-input>
          </bn-field>
          <bn-field class="field-grow" label="Issuer">
            <bn-input
              type="search"
              .value=${(f.issuer ?? [])[0] ?? ""}
              @input=${(e: CustomEvent<{ value: string }>) => this.setText("issuer", e.detail.value)}
            ></bn-input>
          </bn-field>
          <bn-field class="field-grow" label="Group">
            <bn-input
              type="search"
              .value=${(f.group ?? [])[0] ?? ""}
              @input=${(e: CustomEvent<{ value: string }>) => this.setText("group", e.detail.value)}
            ></bn-input>
          </bn-field>
          <bn-field class="field-sm" label="Truncated">
            <bn-select
              .options=${[
                { value: ANY, label: "Any" },
                { value: "true", label: "Truncated" },
                { value: "false", label: "Not truncated" },
              ]}
              .value=${f.truncated === undefined ? ANY : String(f.truncated)}
              @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
                this.filter = {
                  ...this.filter,
                  truncated: e.detail.value === ANY ? undefined : e.detail.value === "true",
                };
                this.refilter();
              }}
            ></bn-select>
          </bn-field>
          <bn-field class="field-sm" label="Min duration (ms)">
            <bn-input
              type="number"
              inputmode="numeric"
              .value=${f.min_duration_ms ? String(f.min_duration_ms) : ""}
              @input=${(e: CustomEvent<{ value: string }>) => {
                const n = Number(e.detail.value);
                this.filter = {
                  ...this.filter,
                  min_duration_ms: e.detail.value && n >= 0 ? n : undefined,
                };
                clearTimeout(this.typeTimer);
                this.typeTimer = window.setTimeout(() => this.refilter(), TYPE_DEBOUNCE_MS);
              }}
            ></bn-input>
          </bn-field>
          <bn-field class="field-md" label="Sort">
            <bn-select
              .options=${[
                { value: "-ts", label: "Newest first" },
                { value: "ts", label: "Oldest first" },
                { value: "-duration_ms", label: "Slowest first" },
              ]}
              .value=${f.sort ?? "-ts"}
              @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
                this.filter = { ...this.filter, sort: e.detail.value as AuditFilter["sort"] };
                this.refilter();
              }}
            ></bn-select>
          </bn-field>
          <bn-field
            class="field-grow"
            label="SQL text contains"
            hint=${blocked ||
            `Scans sql_sample, case-insensitively. Capped at ${TEXT_SEARCH_WINDOW_DAYS} days because it cannot be answered from an index.`}
          >
            <bn-input
              type="search"
              ?disabled=${!!blocked}
              .value=${f.q ?? ""}
              @input=${(e: CustomEvent<{ value: string }>) => {
                this.filter = { ...this.filter, q: e.detail.value || undefined };
                clearTimeout(this.typeTimer);
                this.typeTimer = window.setTimeout(() => this.refilter(), TYPE_DEBOUNCE_MS);
              }}
            ></bn-input>
          </bn-field>
        </div>
        <p class="meta">
          Four declared event types — <code>policy-decision</code>, <code>config-reload</code>,
          <code>auth-event</code> and <code>datasource-connect</code> — are deliberately
          <strong>not offered</strong>: they exist in Sluice's record type and nothing in this build
          writes them (<code>SLUICE-U09</code>), so filtering on one could only ever return nothing.
          <code>request_id</code> is absent for a stronger reason: it is not in the audit record at
          all. Correlate through the query id.
        </p>
      </details>
    `;
  }

  /* ── records ─────────────────────────────────────────────────────────── */

  private get filtered(): boolean {
    const f = this.filter;
    return !!(
      f.subject?.length ||
      f.issuer?.length ||
      f.method?.length ||
      f.decision?.length ||
      f.error_code?.length ||
      f.event_type?.length ||
      f.catalog?.length ||
      f.schema?.length ||
      f.table?.length ||
      f.policy?.length ||
      f.query_id?.length ||
      f.origin?.length ||
      f.group?.length ||
      f.truncated !== undefined ||
      f.min_duration_ms ||
      f.q
    );
  }

  private toRows(): BnRow[] {
    return (this.page?.items ?? []).map((r) => ({
      id: r.id,
      ts: html`<span class="cell-sub">${whenUtc(r.ts)}</span>`,
      decision: r.decision
        ? html`<bn-badge .variant=${decisionVariant(r.decision)}>${r.decision}</bn-badge>
            ${r.error_code ? html`<span class="sub">${r.error_code}</span>` : nothing}`
        : html`<bn-badge variant="neutral">${r.event_type}</bn-badge>`,
      subject: html`<span class="cell-sub">${r.subject.sub || "—"}</span>
        ${r.subject.issuer ? html`<span class="sub">${r.subject.issuer}</span>` : nothing}`,
      tables: this.renderTableCell(r),
      durationCell: duration(r.duration_ms),
      rows: r.rows === undefined ? html`<span class="sub">—</span>` : count(r.rows),
      policies: String((r.applied_policies ?? []).length),
      chain: chainBadge(r.chain.state ?? this.page?.chain_state),
    }));
  }

  /** The tables cell truncates with a `+n` affordance rather than wrapping: a
   * cross-catalog join can touch a dozen tables and the row is a scanning surface,
   * not the record. */
  private renderTableCell(r: AuditRecord) {
    const tables = r.tables ?? [];
    if (tables.length === 0) return html`<span class="sub">—</span>`;
    const shown = tables.slice(0, 2);
    const rest = tables.length - shown.length;
    return html`<span class="cell-sub">${shown.join(", ")}</span>${rest > 0
        ? html`<span class="sub" title=${tables.join("\n")}>+${rest} more</span>`
        : nothing}`;
  }

  private renderEmpty() {
    const ingest = this.page?.ingest;
    if (this.filtered) {
      return html`<bn-empty-state
        icon="⌕"
        heading="No records match these filters"
        message="This instance may still have handled queries in this window — the current filter set excludes all of them."
      >
        <bn-button slot="actions" @click=${this.clearFilters}>Clear filters</bn-button>
      </bn-empty-state>`;
    }
    // `SC-S907`'s table: an audit explorer's zero state has two causes — no
    // traffic, or traffic not yet ingested — and the watermark is what tells them
    // apart. Saying "no queries" without it would be a wrong answer.
    const notIngested = !ingest?.as_of;
    return html`<bn-empty-state
      icon="◌"
      heading=${notIngested ? "Nothing has been ingested yet" : "No records in this window"}
      message=${notIngested
        ? "No audit objects have arrived for this instance. That is not the same as “no queries were handled”: until the first object is ingested this list cannot distinguish the two, which is why the ingest watermark is above it."
        : "This instance's audit was ingested up to the watermark above and holds no records in the selected window. Widen the range to look further back."}
    >
      <bn-button slot="actions" @click=${() => this.setTab("tail")}>
        Watch the live tail
      </bn-button>
    </bn-empty-state>`;
  }

  private renderRecords() {
    if (this.error) {
      return errorCard("Could not search the audit trail", this.error, () => void this.load(this.cursor));
    }
    const page = this.page;
    return html`
      ${page ? watermarkLine(fromIngestStatus(page.ingest)) : nothing}
      ${page ? projectionNotice(page.projection) : nothing}
      ${page && page.chain_state !== "VERIFIED" ? this.renderRangeChainNotice(page) : nothing}
      ${this.renderToolbar()}
      ${page
        ? html`<p class="meta">
            Range applied: ${whenUtc(page.range.from)} → ${whenUtc(page.range.to)}. Pagination is a
            keyset cursor on <code>(ts, hash)</code>, so a record arriving during a scan never causes
            a skip or a repeat — and there is deliberately no total.
          </p>`
        : nothing}
      <bn-card flush>
        <bn-table
          id="records"
          caption="Audit records"
          clickable
          row-key="id"
          ?paginated=${(page?.items.length ?? 0) > 0}
          .columns=${COLUMNS}
          .rows=${this.toRows()}
          .nextCursor=${page?.next || null}
          ?loading=${this.loading}
          @row-click=${(e: CustomEvent<BnRowClickDetail>) => void this.openRecord(String(e.detail.id))}
          @page-change=${(e: CustomEvent<BnPageChangeDetail>) => void this.load(e.detail.cursor)}
        >
          <div slot="empty">${this.renderEmpty()}</div>
        </bn-table>
      </bn-card>
    `;
  }

  /** The range-level chain notice. `SC-S800`'s last criterion is that a gap is
   * shown as a known gap **in the explorer** rather than being invisible, and this
   * is that sentence. */
  private renderRangeChainNotice(page: AuditSearchPage) {
    const broken = page.chain_state === "BROKEN";
    return html`<p
      class=${`notice ${broken ? "notice-error" : "notice-warn"}`}
      role=${broken ? "alert" : "status"}
    >
      Chain state over this range: <strong>${page.chain_state}</strong>.
      ${broken
        ? html`The bytes Sluice Cloud holds for part of this range are not what your gateway wrote.
            Open the chain panel: the records themselves are still shown, because they are the
            evidence.`
        : html`Sluice Cloud's copy is incomplete over this range — records are missing between two
            intact stretches. This is <strong>not tampering</strong>: the sink is best-effort and
            drops on buffer overflow, and an instance killed mid-batch produces exactly this. Your
            instance's own audit volume remains the record of authority.`}
      <a class="chip" href=${`${location.pathname}?tab=chain`} @click=${(e: Event) => {
        e.preventDefault();
        this.setTab("chain");
      }}>Open the chain panel</a>
    </p>`;
  }

  /* ── the record drawer ───────────────────────────────────────────────── */

  private async openRecord(hash: string) {
    this.drawerOpen = true;
    this.detailHash = hash;
    this.detail = null;
    this.detailError = null;
    this.detailLoading = true;
    try {
      this.detail = await getAuditRecord(this.instanceId, hash);
    } catch (err) {
      this.detailError = parseError(err);
    } finally {
      this.detailLoading = false;
    }
  }

  private renderDrawer() {
    const rec = this.detail;
    return html`<bn-drawer
      ?open=${this.drawerOpen}
      heading="Audit record"
      .width=${720}
      @close=${() => (this.drawerOpen = false)}
      @bn-copy=${(e: CustomEvent<BnKvCopyDetail>) => toast(`${e.detail.label} copied`, "success")}
    >
      ${this.detailLoading
        ? html`<bn-spinner block label="Loading the record"></bn-spinner>`
        : this.detailError
          ? errorCard("Could not load this record", this.detailError, () =>
              void this.openRecord(this.detailHash),
            )
          : recordDetailTemplate(this.instanceId, rec, (f) => {
              this.drawerOpen = false;
              this.filter = { ...this.filter, ...f };
              this.refilter();
            })}
      ${rec
        ? html`<bn-button
              slot="footer"
              @click=${() => navigate(hrefFor("instances", this.instanceId, "audit", rec.id))}
            >
              Open as a page
            </bn-button>`
        : nothing}
      <bn-button slot="footer" variant="primary" @click=${() => (this.drawerOpen = false)}>
        Close
      </bn-button>
    </bn-drawer>`;
  }

  /* ── the chain (SC-F803, SC-F807) ────────────────────────────────────── */

  private async loadChain() {
    if (this.chainLoading) return;
    this.chainLoading = true;
    this.chainError = null;
    try {
      this.chain = await getAuditChain(this.instanceId);
    } catch (err) {
      this.chainError = parseError(err);
      this.chain = null;
    } finally {
      this.chainLoading = false;
    }
  }

  /**
   * runVerify starts `audit verify` and watches the job.
   *
   * The job **succeeds in both outcomes** — a verification that ran and found a
   * break did its job — so a failed chain is not an error path here. `jobFailure`
   * is only consulted for a job that could not run at all.
   */
  private async runVerify() {
    this.verifying = true;
    this.verifyError = null;
    this.verifyResult = null;
    this.verifyStep = "queued";
    try {
      const started = await verifyAuditChain(this.instanceId, { scope: "cloud" });
      const done = await watchJob<VerifyResponse>(started.job.id, {
        onUpdate: (j) => (this.verifyStep = j.progress?.step ?? j.status),
      });
      const failure = jobFailure(done);
      if (failure) {
        this.verifyError = failure;
      } else {
        this.verifyResult = done.result ?? null;
        void this.loadChain();
      }
    } catch (err) {
      this.verifyError = parseError(err);
    } finally {
      this.verifying = false;
      this.verifyStep = "";
    }
  }

  private renderChain() {
    if (this.chainError) {
      return errorCard("Could not read the chain registry", this.chainError, () => void this.loadChain());
    }
    if (!this.chain) {
      return html`<bn-spinner block label="Reading the chain registry"></bn-spinner>`;
    }
    const c = this.chain;
    const verifiedSpans = c.chain_segments.length;
    return html`
      ${watermarkLine(fromIngestStatus(c.ingest))}
      <bn-card heading="Chain state">
        <bn-button slot="actions" variant="primary" ?loading=${this.verifying} @click=${this.runVerify}>
          ${this.verifying ? `Verifying… ${this.verifyStep}` : "Verify now"}
        </bn-button>
        <div class="row">
          ${chainBadge(c.continuity)} ${profileBadge(c.profile)}
          ${verifiedSpans > 1
            ? html`<bn-badge variant="info">${verifiedSpans} chain segments</bn-badge>`
            : nothing}
        </div>
        <p class="meta">
          ${verifiedSpans > 1
            ? html`This instance's audit is an <strong>ordered list of ${verifiedSpans} chain
                  segments</strong>, not one chain. A restart on an empty state volume opens a new
                segment from the same registered anchor; no hash joins two segments because none was
                ever computed. Each verifies from its own anchor.`
            : html`One chain segment, verifying from the registered genesis anchor.`}
        </p>
        <bn-kv
          .rows=${[
            { label: "Genesis anchor", value: c.genesis.anchor_hash },
            { label: "Seed fingerprint", value: c.genesis.seed_fingerprint },
            { label: "Anchor minted", value: whenUtc(c.genesis.minted), copyable: false },
            { label: "Objects held", value: count(c.ingest.objects), copyable: false },
            { label: "Records held", value: count(c.ingest.records), copyable: false },
          ] as BnKvRow[]}
        ></bn-kv>
        ${c.last_verified
          ? html`<bn-kv
              .rows=${[
                { label: "Last verified hash", value: c.last_verified.hash },
                { label: "Last verified at", value: whenUtc(c.last_verified.at), copyable: false },
              ] as BnKvRow[]}
            ></bn-kv>`
          : html`<p class="notice notice-warn">
              <strong>This chain has never been verified.</strong> That is a distinct state, and it
              is stated rather than implied: an unverified chain shown as implicitly fine would be
              the same dishonesty as calling a step that opens no socket a connection test. Run a
              verification to establish a watermark.
            </p>`}
        <p class="meta">
          A verified chain proves that the records shown here are exactly the bytes your gateway
          wrote, in the order it wrote them, and that no record has been altered, inserted or removed
          <em>from within</em> the verified range. It does <strong>not</strong> prove the range is
          complete — Sluice Cloud holds a best-effort copy, and a gap is a gap and not a break. It
          does <strong>not</strong> prove records could not have been withheld at the source: the
          chain is written by the gateway, which also holds the seed. What the control plane adds is
          the dated notarization above — a third party recording what it saw and when, which is what
          makes tail truncation and a fresh-seed regeneration detectable.
        </p>
      </bn-card>
      ${this.renderVerifyResult()} ${this.renderSpans(c)} ${this.renderLedger(c)}
    `;
  }

  /**
   * renderVerifyResult reports the **shape `audit verify` actually produces**: the
   * anchor used, the file and record counts, the last verified hash, and on failure
   * the exact file and line (`SC-F807`, `concept.md` §6.6).
   *
   * A green badge with no explanation of what was checked is what the compliance
   * persona explicitly churns on, so there is no bare badge here.
   *
   * `invalid_json` is rendered **calmly**. The server's own explanation says the
   * usual cause is a verification that raced the file sink's buffered write and read
   * a partial final line — a false BROKEN is worse than no verification at all, so
   * this shape gets a warning and a "re-run before treating it as an incident",
   * never the red alarm.
   */
  private renderVerifyResult() {
    if (this.verifyError) {
      return errorCard("The verification could not run", this.verifyError, () => void this.runVerify());
    }
    const v = this.verifyResult;
    if (!v) return nothing;
    const brk = v.break;
    const torn = brk?.class === "invalid_json";
    return html`<bn-card heading="Verification result">
      <div class="row">
        ${v.ok
          ? html`<bn-badge variant="success">chain verified</bn-badge>`
          : torn
            ? html`<bn-badge variant="warning">unparseable line — probably a torn write</bn-badge>`
            : html`<bn-badge variant="error">chain broken</bn-badge>`}
        <bn-badge variant="neutral">scope: ${v.scope}</bn-badge>
        ${v.recorded
          ? html`<bn-badge variant="info">notarized</bn-badge>`
          : html`<bn-badge variant="neutral">not recorded</bn-badge>`}
      </div>
      <bn-kv
        .rows=${[
          { label: "Anchor used", value: v.anchor },
          {
            label: "Files walked",
            value: v.ok ? count(v.files) : "not reported on a failure",
            copyable: false,
          },
          {
            label: "Records walked",
            value: v.ok ? count(v.records) : "not reported on a failure",
            copyable: false,
          },
          { label: "Last verified hash", value: v.last_hash ?? "" },
          { label: "Range", value: v.from || v.to ? `${when(v.from)} → ${when(v.to)}` : "whole chain", copyable: false },
          { label: "Duration", value: duration(v.duration_ms), copyable: false },
          { label: "CLI run id", value: v.run_id ?? "" },
        ] as BnKvRow[]}
      ></bn-kv>
      <p class="meta">
        The anchor is echoed because a verification's strength is a function of it: with no anchor the
        walk accepts whatever the first record claims as its predecessor, so a wholesale substituted
        chain would verify clean.
        ${v.ok
          ? nothing
          : html` The counters read “not reported” rather than zero: the CLI's failure object carries
              no counts at all, so nothing here says how far the walk got — only the break below
              does.`}
        ${v.recorded
          ? nothing
          : html` This outcome was <strong>not</strong> filed with the anchor registry, so the next
              verification re-walks from genesis.`}
      </p>
      ${brk
        ? html`<div
            class=${`notice ${torn ? "notice-warn" : "notice-error"}`}
            role=${torn ? "status" : "alert"}
          >
            <p>
              <strong>${brk.file}</strong>, line <strong>${brk.line}</strong> —
              <code>${brk.class}</code>
            </p>
            <p>${brk.explanation}</p>
            <p class="mono">${brk.error}</p>
            <bn-kv
              .rows=${[
                ...(brk.stored ? [{ label: "Stored hash", value: brk.stored }] : []),
                ...(brk.recomputed ? [{ label: "Recomputed hash", value: brk.recomputed }] : []),
                ...(brk.prior_hash ? [{ label: "Record's prior_hash", value: brk.prior_hash }] : []),
                ...(brk.expected ? [{ label: "Expected", value: brk.expected }] : []),
              ] as BnKvRow[]}
            ></bn-kv>
            <p>${brk.advice}</p>
            ${brk.repairable
              ? nothing
              : html`<p class="meta">
                  There is no repair action and there will not be one: repairing, re-hashing or
                  re-chaining the affected segment destroys the evidentiary value that made this
                  finding worth reporting.
                </p>`}
            ${torn
              ? html`<div class="row row-actions">
                  <bn-button ?loading=${this.verifying} @click=${this.runVerify}>
                    Re-run the verification
                  </bn-button>
                </div>`
              : nothing}
          </div>`
        : nothing}
    </bn-card>`;
  }

  /** renderSpans lists the chain segments. A `volume_loss` span with no explaining
   * event is a finding rather than routine, and the copy says so. */
  private renderSpans(c: ChainStatus) {
    if (c.chain_segments.length === 0) return nothing;
    return html`<bn-card heading="Chain segments">
      <div class="span-grid">
        ${c.chain_segments.map(
          (s) => html`<div class="span">
            <bn-badge variant="neutral">#${s.ordinal}</bn-badge>
            <span class="mono">${shortHash(s.first_hash)} → ${shortHash(s.last_hash)}</span>
            <span class="meta">${count(s.record_count)} records</span>
            <span class="meta">${whenUtc(s.first_record_at)} → ${whenUtc(s.last_record_at)}</span>
            <bn-badge .variant=${s.started_by === "volume_loss" && !s.started_by_event ? "warning" : "info"}>
              started by ${s.started_by}
            </bn-badge>
            ${s.started_by === "volume_loss" && !s.started_by_event
              ? html`<span class="meta">
                  No migration or restore event explains this segment boundary. A new chain opened on
                  an empty state volume with no recorded cause is worth investigating, and it is not
                  the same finding as a gap.
                </span>`
              : nothing}
          </div>`,
        )}
      </div>
      <p class="meta">
        A boundary between two segments is <strong>not a gap and not tampering</strong>. Sluice Cloud
        injects a stable genesis seed, so an instance that boots on an empty volume legitimately
        opens a second segment from the same registered anchor. Each side verifies independently and
        neither is stitched to the other.
      </p>
    </bn-card>`;
  }

  /** renderLedger is the object-level ledger. A pruned row stays forever: the
   * boundary hash it carries is what lets the remaining archive keep verifying. */
  private renderLedger(c: ChainStatus) {
    if (c.segments.length === 0) {
      return html`<bn-panel eyebrow="Object ledger">
        <p class="meta">
          No ingested objects are recorded for this instance yet. Under the Customer-held profile
          there will never be any, and the chain attestation above is the whole surface by design.
        </p>
      </bn-panel>`;
    }
    const recovered = c.segments.flatMap((s) => s.recovered_ranges ?? []);
    return html`<bn-card heading="Object ledger">
      <div class="scroll">
        <table class="ledger">
          <thead>
            <tr>
              <th scope="col">Seq</th>
              <th scope="col">State</th>
              <th scope="col">Object</th>
              <th scope="col">Records</th>
              <th scope="col">First → last record</th>
              <th scope="col">Final hash</th>
              <th scope="col">Verified</th>
            </tr>
          </thead>
          <tbody>
            ${c.segments.map(
              (s) => html`<tr>
                <td>${s.seq}</td>
                <td>${s.state}</td>
                <td title=${s.object_key}>${s.object_key.split("/").pop()}</td>
                <td>${count(s.record_count)}</td>
                <td>${whenUtc(s.first_record_at)} → ${whenUtc(s.last_record_at)}</td>
                <td title=${s.final_hash}>${shortHash(s.final_hash)}</td>
                <td>${s.verified_at ? whenUtc(s.verified_at) : "—"}</td>
              </tr>`,
            )}
          </tbody>
        </table>
      </div>
      <p class="meta">
        A <code>pruned</code> row is kept permanently. Retention removes whole objects, oldest first,
        and the last pruned record's hash equals the first remaining record's <code>prior_hash</code>
        — deleting the row would break anchored verification for good on the first retention run.
      </p>
      ${recovered.length > 0
        ? html`<p class="notice notice-warn">
            ${recovered.length} recovered range(s), covering
            ${count(recovered.reduce((a, r) => a + r.record_count, 0))} records, were repaired through
            the instance's admin tail and re-canonicalized here. They verify, but “byte-identical to
            what your instance wrote” does not hold for them, and an export overlapping them says so
            in its manifest.
          </p>`
        : nothing}
    </bn-card>`;
  }

  /* ── the live tail (SC-F823) ─────────────────────────────────────────── */

  private startTail() {
    if (this.tailRunning) return;
    this.tailRunning = true;
    void this.pollTail();
  }

  private stopTail() {
    this.tailRunning = false;
    clearTimeout(this.tailTimer);
  }

  /**
   * pollTail is `SC-F823`'s loop: `n=200` every 3 s, de-duplicated on `hash`,
   * appended.
   *
   * It polls rather than streams because the streaming form is refused explicitly
   * by the server — answering a plain page to a client that asked to follow would
   * leave it polling a stream it believes is pushing.
   */
  private async pollTail() {
    if (!this.tailRunning) return;
    try {
      const res = await getAuditTail(this.instanceId, { n: TAIL_DEFAULT_N });
      this.tailMeta = res;
      this.tailError = null;
      const seen = new Set(this.tailItems.map((r) => r.hash));
      const fresh = res.items.filter((r) => !seen.has(r.hash));
      if (fresh.length > 0) {
        this.tailItems = [...fresh.reverse(), ...this.tailItems].slice(0, TAIL_BUFFER);
      }
    } catch (err) {
      this.tailError = parseError(err);
      // A tail failure stops the loop rather than hammering an unreachable
      // instance. The explorer is unaffected: it reads the ingested copy.
      this.tailRunning = false;
      return;
    }
    if (this.tailRunning) {
      this.tailTimer = window.setTimeout(() => void this.pollTail(), TAIL_POLL_MS);
    }
  }

  private renderTail() {
    const meta = this.tailMeta;
    return html`
      <bn-panel eyebrow="Live tail">
        <p>
          ${meta?.notice ??
          "live tail — may skip records under high volume; the explorer is complete"}
        </p>
        <p class="meta">
          Two limits, stated here rather than in a help page: it is
          <strong>last-N, capped at 1000 records</strong>, and it is
          <strong>lossy across a restart</strong>. It reads the instance's own admin plane, so it has
          <strong>zero ingest lag</strong> — which is what makes it the right tool for “change a
          policy, run a query, see the decision” and the wrong tool for anything durable. Its JSON is
          re-marshalled struct output, not the wire bytes the hash chain covers, so a record here
          cannot be exported, verified, or used to support a chain claim. The
          <a class="chip" href=${`${location.pathname}?tab=records`} @click=${(e: Event) => {
            e.preventDefault();
            this.setTab("records");
          }}>explorer</a>
          is authoritative.
        </p>
        <div class="row row-actions">
          ${this.tailRunning
            ? html`<bn-button @click=${this.stopTail}>Pause</bn-button>`
            : html`<bn-button variant="primary" @click=${this.startTail}>Start</bn-button>`}
          <bn-button @click=${() => (this.tailItems = [])}>Clear</bn-button>
          ${this.tailRunning
            ? html`<span class="meta"><bn-badge variant="success" dot>polling every 3 s</bn-badge></span>`
            : nothing}
          ${meta?.capped
            ? html`<bn-badge variant="warning">n was clamped to the server's cap</bn-badge>`
            : nothing}
          ${meta ? profileBadge(meta.profile) : nothing}
        </div>
        ${meta ? projectionNotice(meta.projection) : nothing}
        ${meta && meta.ingest.lag_seconds !== null
          ? html`<p class="meta">
              The ingested copy is ${ago(meta.ingest.lag_seconds)} behind this view. A record you see
              here appears in the explorer after that delay; if it never does, that is a gap and the
              chain panel is where it shows.
            </p>`
          : nothing}
      </bn-panel>
      ${this.tailError
        ? errorCard("The live tail is unavailable", this.tailError, () => this.startTail())
        : html`<bn-card flush>
            <bn-table
              caption="Live audit tail"
              row-key="hash"
              .columns=${TAIL_COLUMNS}
              .rows=${this.tailItems.map((r) => ({
                hash: r.hash,
                ts: html`<span class="cell-sub">${whenUtc(r.ts)}</span>`,
                decision: r.decision
                  ? html`<bn-badge .variant=${decisionVariant(r.decision)}>${r.decision}</bn-badge>
                      ${r.error_code ? html`<span class="sub">${r.error_code}</span>` : nothing}`
                  : html`<bn-badge variant="neutral">${r.event_type}</bn-badge>`,
                subject: html`<span class="cell-sub">${r.subject.sub || "—"}</span>`,
                tables: html`<span class="cell-sub">${(r.tables ?? []).join(", ") || "—"}</span>`,
                policies: html`<span class="cell-sub"
                  >${(r.applied_policies ?? []).join(", ") || "—"}</span
                >`,
                durationCell: duration(r.duration_ms),
              }))}
              empty="Nothing yet. Start the tail and run a query against the instance."
            ></bn-table>
          </bn-card>`}
    `;
  }

  /* ── the export (SC-F805) ────────────────────────────────────────────── */

  private async runExport(verification?: string) {
    this.exporting = true;
    this.exportError = null;
    this.exportResult = null;
    try {
      const started = await exportAudit(
        this.instanceId,
        {
          format: this.exportFormat,
          from: this.filter.from,
          to: this.filter.to,
          // A JSONL export cannot be filtered — the records a filter drops are the
          // prior_hash of the records it keeps — so the filter travels only with a
          // CSV. The dialog says so before the button is pressed.
          ...(this.exportFormat === "csv" ? { filter: this.csvFilter() } : {}),
        },
        { verification },
      );
      const done = await watchJob<ExportResult>(started.job.id);
      const failure = jobFailure(done);
      if (failure) this.exportError = failure;
      else this.exportResult = done.result ?? {};
      this.stepUpPhase = "";
    } catch (err) {
      const e = parseError(err);
      if (e.code === "verification_required") {
        this.stepUpPhase = "needed";
      } else {
        this.exportError = e;
      }
    } finally {
      this.exporting = false;
    }
  }

  /**
   * csvFilter is the current filter as the export body's `filter` map.
   *
   * The predicate keys are listed rather than derived from the filter object,
   * because the filter also carries the window and the paging — and `sort`,
   * `limit` and `cursor` are in the server's allowlist, so passing them would be
   * accepted and mean nothing on an export. `from` and `to` are top-level fields
   * on the body, not filters.
   */
  private csvFilter(): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    const f = this.filter;
    for (const key of [
      "subject",
      "issuer",
      "method",
      "decision",
      "error_code",
      "event_type",
      "catalog",
      "schema",
      "table",
      "policy",
      "query_id",
      "origin",
      "group",
    ] as const) {
      const v = (f[key] ?? []) as string[];
      if (v.length > 0) out[key] = v;
    }
    if (f.truncated !== undefined) out["truncated"] = [String(f.truncated)];
    if (f.min_duration_ms) out["min_duration_ms"] = [String(f.min_duration_ms)];
    if (f.q) out["q"] = [f.q];
    return out;
  }

  private async stepUpWithPasskey() {
    try {
      const grant = await verifyWithPasskey({ action: "export_audit", target: this.instanceId });
      await this.runExport(grant);
    } catch (err) {
      this.exportError = parseError(err);
    }
  }

  private async sendOtp() {
    try {
      const { otpId } = await requestStepUpOtp();
      this.otpId = otpId;
      this.stepUpPhase = "otp";
    } catch (err) {
      this.exportError = parseError(err);
    }
  }

  private async confirmOtp() {
    try {
      const grant = await confirmStepUpOtp(this.otpId, this.otpCode, {
        action: "export_audit",
        target: this.instanceId,
      });
      this.otpCode = "";
      await this.runExport(grant);
    } catch (err) {
      this.exportError = parseError(err);
    }
  }

  private renderExportModal() {
    return html`<bn-modal
      ?open=${this.exportOpen}
      heading="Export audit records"
      .width=${560}
      @close=${() => (this.exportOpen = false)}
    >
      <bn-field
        label="Format"
        hint="JSONL is the evidence format: the raw lines, byte for byte, in chain order, with a sidecar manifest. CSV is the spreadsheet."
      >
        <bn-select
          .options=${[
            {
              value: "jsonl",
              label: "JSONL — audit evidence",
              description:
                "The raw T0 lines unchanged, so it feeds `sluice audit verify` directly. Cannot be filtered.",
            },
            {
              value: "csv",
              label: "CSV — spreadsheet",
              description: "The index columns of the current filter. Not verifiable evidence.",
            },
          ]}
          .value=${this.exportFormat}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
            (this.exportFormat = e.detail.value as ExportFormat)}
        ></bn-select>
      </bn-field>
      <bn-kv
        .rows=${[
          { label: "From (inclusive)", value: whenUtc(this.filter.from), copyable: false },
          { label: "To (exclusive)", value: whenUtc(this.filter.to), copyable: false },
        ] as BnKvRow[]}
      ></bn-kv>
      ${this.exportFormat === "jsonl"
        ? html`<p class="notice">
            <strong>A JSONL export is not filtered, and cannot be.</strong> The records a filter
            drops are the <code>prior_hash</code> of the records it keeps, so a filtered JSONL export
            would verify as <code>BROKEN</code> — and the person holding it would be an auditor.
            Export the range, and filter the CSV instead.
          </p>`
        : html`<p class="notice">
            The current filter set is applied to this CSV. It is not chain evidence: a filtered
            export is a subset, and only the unfiltered JSONL verifies.
          </p>`}
      <p slot="consequence">
        This moves a bulk copy of your instance's query history — subjects, tables, timestamps and,
        under the Standard profile, SQL samples — out of the product. It is recorded in your
        organization's activity feed with the range, the format and the profile.
      </p>
      ${this.stepUpPhase === "needed"
        ? html`<div class="notice">
            <p>
              An export needs <strong>step-up verification</strong>. A leaked session that could
              silently export the audit trail would defeat the purpose of having one.
            </p>
            <div class="row">
              <bn-button @click=${this.stepUpWithPasskey}>Verify with a passkey</bn-button>
              <bn-button @click=${this.sendOtp}>Email me a code</bn-button>
            </div>
          </div>`
        : nothing}
      ${this.stepUpPhase === "otp"
        ? html`<bn-field label="Verification code" hint="Six digits, valid for a few minutes.">
              <bn-input
                inputmode="numeric"
                .value=${this.otpCode}
                @input=${(e: CustomEvent<{ value: string }>) => (this.otpCode = e.detail.value)}
              ></bn-input>
            </bn-field>
            <div class="row">
              <bn-button variant="primary" @click=${this.confirmOtp}>Confirm and export</bn-button>
            </div>`
        : nothing}
      ${this.exportError
        ? html`<div class="notice notice-error" role="alert">
            <p>${this.exportError.message}</p>
            <p class="meta">
              <code>${this.exportError.code}</code>${this.exportError.requestId
                ? html` · request ${this.exportError.requestId}`
                : nothing}
            </p>
          </div>`
        : nothing}
      ${this.exportResult
        ? html`<div class="notice">
            <p><strong>Export ready.</strong></p>
            <bn-kv
              .rows=${[
                { label: "Records", value: count(this.exportResult.records), copyable: false },
                { label: "Size", value: bytes(this.exportResult.bytes), copyable: false },
                { label: "Link expires", value: when(this.exportResult.expires), copyable: false },
              ] as BnKvRow[]}
            ></bn-kv>
            ${this.exportResult.url
              ? html`<div class="row row-actions">
                  <a class="chip" href=${this.exportResult.url} download>Download</a>
                </div>`
              : nothing}
          </div>`
        : nothing}
      <bn-button slot="footer" @click=${() => (this.exportOpen = false)}>Close</bn-button>
      <bn-button
        slot="footer"
        variant="primary"
        ?loading=${this.exporting}
        ?disabled=${this.stepUpPhase === "otp"}
        @click=${() => void this.runExport()}
      >
        Start export
      </bn-button>
    </bn-modal>`;
  }

  /* ── frame ───────────────────────────────────────────────────────────── */

  private setTab(tab: TabId) {
    this.tab = tab;
    this.writeUrl();
    if (tab === "chain" && !this.chain) void this.loadChain();
    if (tab !== "tail") this.stopTail();
  }

  private renderProfileNote(): TemplateResult | typeof nothing {
    const profile = this.page?.profile ?? this.chain?.profile ?? this.instance?.audit_profile;
    if (profile !== "minimized" && profile !== "customer_held") return nothing;
    return html`<p class="notice">
      ${profile === "minimized"
        ? html`<strong>Minimized profile.</strong> No SQL text is captured at the source, so the SQL
            column and the text search are unavailable <em>by your organization's own choice</em> —
            not because data is missing. Every other filter, chart and breakdown works: a
            fingerprint is not the text.`
        : html`<strong>Customer-held profile.</strong> Sluice Cloud stores no audit records for this
            instance; they are in your own bucket with your own credentials. The chain attestation is
            real and is the surface with something to say.`}
    </p>`;
  }

  override render() {
    const tabs: BnTab[] = [
      { id: "records", label: "Records" },
      { id: "chain", label: "Chain & verification" },
      { id: "tail", label: "Live tail" },
    ];
    return html`
      <header>
        <div class="head-text">
          <h1>Audit</h1>
          <p class="lede">
            Every query this instance decided on, as its gateway recorded it. Sluice Cloud holds a
            best-effort copy of a hash chain your instance wrote and owns: the copy is what you
            search here, and your instance's own audit volume remains the record of authority.
          </p>
        </div>
        <div class="head-actions">
          ${profileBadge(this.page?.profile ?? this.chain?.profile ?? this.instance?.audit_profile)}
          ${this.page ? chainBadge(this.page.chain_state) : nothing}
          <bn-button
            @click=${() => {
              this.exportOpen = true;
              this.exportResult = null;
              this.exportError = null;
              this.stepUpPhase = "";
            }}
          >
            Export…
          </bn-button>
          <bn-button
            @click=${() => navigate(hrefFor("instances", this.instanceId, "analytics"))}
          >
            Analytics
          </bn-button>
        </div>
      </header>
      ${this.renderProfileNote()}
      <bn-tabs
        label="Audit surfaces"
        .tabs=${tabs}
        .active=${this.tab}
        @change=${(e: CustomEvent<BnTabsChangeDetail>) => this.setTab(e.detail.id as TabId)}
      >
        <div slot="records" class="tab-body">${this.renderRecords()}</div>
        <div slot="chain" class="tab-body">${this.renderChain()}</div>
        <div slot="tail" class="tab-body">${this.renderTail()}</div>
      </bn-tabs>
      ${this.renderDrawer()} ${this.renderExportModal()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-audit": PageAudit;
  }
}
